import { Channel } from '@flowus/common/block/channel';
import { cx } from '@flowus/common/cx';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { SPECIAL_SPACE_ID } from 'src/common/const';
import { request } from 'src/common/request/request';
import { useCurrentSpace } from 'src/hooks/space/use-current-space';
import { invalidateNotifications } from 'src/services/app';
import { NotificationBox } from './message-notification';
import { useRefreshUnreadState, useUnreadState } from './shared';

export enum MessagePanelId {
  Mention = 0,
  Share,
  Inbox,
  Subscribe,
}

interface Props {
  channels?: Channel[];
  defaultChannel?: Channel;
}

/** 消息通知面板 */
export const MessagePanel: FC<Props> = (props) => {
  const {
    channels = [Channel.MENTIONS, Channel.SHARING, Channel.INBOX, Channel.SUBSCRIBE],
    defaultChannel = channels[0] ?? Channel.MENTIONS,
  } = props;
  const unread = useUnreadState();
  const currentSpace = useCurrentSpace();
  const unreadMentionCount = unread[currentSpace.uuid]?.mention ?? 0;
  const unreadShareCount = unread[currentSpace.uuid]?.share ?? 0;
  const unreadInboxCount = unread[currentSpace.uuid]?.inbox ?? 0;
  const unreadSubscribeCount = unread[SPECIAL_SPACE_ID]?.subscribe ?? 0;
  const [tabIndex, setTabIndex] = useState(defaultChannel);
  const refreshUnreadState = useRefreshUnreadState();

  useEffect(() => {
    if (unreadMentionCount > 0) {
      return;
    }
    if (unreadShareCount > 0) {
      setTabIndex(Channel.SHARING);
      return;
    }
    if (unreadInboxCount > 0) {
      setTabIndex(Channel.INBOX);
      return;
    }
    if (unreadSubscribeCount > 0) {
      setTabIndex(Channel.SUBSCRIBE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let spaceId = currentSpace.uuid;

    if (tabIndex === Channel.SUBSCRIBE) {
      spaceId = SPECIAL_SPACE_ID;
    }

    void request.editor
      .setNotificationRead({
        spaceId,
        channel: tabIndex,
        timestamp: Date.now(),
      })
      .then(() => {
        invalidateNotifications();
        void refreshUnreadState();
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  const renderList = useMemo(() => {
    return [
      {
        id: Channel.MENTIONS,
        name: '协作通知',
        count: unreadMentionCount,
      },
      {
        id: Channel.SHARING,
        name: '分享通知',
        className: 'sharing-notification',
        count: unreadShareCount,
      },
      {
        id: Channel.INBOX,
        name: '收件箱',
        className: 'inbox-notification',
        count: unreadInboxCount,
      },
      {
        id: Channel.SUBSCRIBE,
        name: '订阅通知',
        count: unreadSubscribeCount,
      },
    ].filter((item) => channels.includes(item.id));
  }, [channels, unreadInboxCount, unreadMentionCount, unreadShareCount, unreadSubscribeCount]);

  return (
    <div
      className="relative w-[500px] min-h-[400px] next-modal flex flex-col"
      style={{ maxHeight: 'calc(100vh - 100px)' }}
    >
      <div className="h-[50px] border-b border-grey6 flex py-2.5 justify-between">
        <span className="flex items-center space-x-2 px-2">
          {renderList.map((item) => (
            <span
              key={item.id}
              onClick={() => setTabIndex(item.id)}
              className={cx('text-t2 py-1 px-2 space-x-2 rounded-sm cursor-pointer', {
                'bg-black_006': tabIndex === item.id,
              })}
            >
              <span>{item.name}</span>
              {item.count > 0 && (
                <span className="min-w-[18px] px-0.5 inline-flex items-center justify-center text-white bg-red text-t4-medium rounded-full">
                  {item.count}
                </span>
              )}
            </span>
          ))}
        </span>
      </div>
      <div className="flex-1 min-h-0 flex flex-col select-text">
        <div className="overflow-auto flex-1 min-h-[200px]">
          {renderList.map((item) => (
            <div key={item.id} className={cx(item.className, { hidden: tabIndex !== item.id })}>
              <NotificationBox channel={item.id} />
            </div>
          ))}
          <div className="text-t4 text-grey3 text-center py-2">没有更多了…</div>
        </div>
      </div>
    </div>
  );
};
